// ============================================================
//  src/pages/bots/tabs/LogsTab.js  — Webhook Activity Log
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { listLogs, clearLogs } from '../../../api/client';
import toast from 'react-hot-toast';
import './LogsTab.css';

// ── Constants ────────────────────────────────────────────────
const LEVELS   = ['', 'debug', 'info', 'warn', 'error'];
const TRIGGERS = ['', 'incoming', 'send_message', 'http_request', 'condition', 'system'];
const POLL_MS  = 8000;

// ── Helpers ──────────────────────────────────────────────────
function levelMeta(level) {
  return {
    debug: { label: 'DEBUG', color: '#9AA0C0', bg: 'rgba(154,160,192,0.10)', dot: '#9AA0C0' },
    info:  { label: 'INFO',  color: '#00C88C', bg: 'rgba(0,200,140,0.10)',   dot: '#00C88C' },
    warn:  { label: 'WARN',  color: '#FF9500', bg: 'rgba(255,149,0,0.10)',   dot: '#FF9500' },
    error: { label: 'ERROR', color: '#FF3B30', bg: 'rgba(255,59,48,0.10)',   dot: '#FF3B30' },
  }[level] ?? { label: level?.toUpperCase() ?? '—', color: '#9AA0C0', bg: 'rgba(154,160,192,0.10)', dot: '#9AA0C0' };
}

function directionMeta(dir) {
  if (dir === 'in')  return { icon: '↓', label: 'Received', color: '#00C88C' };
  if (dir === 'out') return { icon: '↑', label: 'Sent',     color: '#007AFF' };
  return { icon: '·', label: '',        color: '#9AA0C0' };
}

function formatTime(str) {
  if (!str) return '—';
  const d = new Date(str.replace(' ', 'T') + 'Z');
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatTimeShort(str) {
  if (!str) return '—';
  const d   = new Date(str.replace(' ', 'T') + 'Z');
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function parseMeta(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
}

function Spinner({ size = 16, color = 'var(--primary)' }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      border: `2px solid ${color}30`, borderTopColor: color,
      borderRadius: '50%', animation: 'logspin 0.65s linear infinite',
      flexShrink: 0,
    }} />
  );
}

// ── Sub-components ───────────────────────────────────────────
function StatPill({ label, value, color }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 2, padding: '10px 18px',
      background: 'var(--surface)', borderRadius: 10,
      border: '1.5px solid var(--border)', minWidth: 72,
    }}>
      <span style={{ fontSize: 20, fontWeight: 800, color: color ?? 'var(--text-dark)', lineHeight: 1 }}>
        {value}
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </span>
    </div>
  );
}

function LogRow({ log, expanded, onToggle }) {
  const meta    = parseMeta(log.lMeta);
  const dir     = meta?.direction ?? null;
  const dirM    = directionMeta(dir);
  const levelM  = levelMeta(log.lLevel);
  const isIn    = dir === 'in';

  return (
    <div
      className={`wl-row wl-row--${log.lLevel} ${expanded ? 'wl-row--open' : ''}`}
      onClick={onToggle}
    >
      {/* Direction stripe */}
      <div className="wl-stripe" style={{ background: dirM.color }} />

      {/* Main row */}
      <div className="wl-row-main">

        {/* Meta cluster: direction, level, trigger -- always stays together */}
        <div className="wl-meta-group">
          <div className="wl-dir" title={dirM.label} style={{ color: dirM.color }}>
            {dirM.icon}
          </div>
          <span className="wl-badge" style={{ color: levelM.color, background: levelM.bg }}>
            {levelM.label}
          </span>
          {log.lTrigger && (
            <span className="wl-chip">
              {isIn ? '📨' : log.lTrigger === 'system' ? '⚙️' : log.lTrigger === 'error' ? '🔴' : '📤'}&nbsp;
              {log.lTrigger}
            </span>
          )}
        </div>

        {/* Message -- always gets its own line's worth of room; never
            squeezed to unreadable width by neighbouring pills */}
        <span className="wl-msg">
          {meta?.message || log.lMessage || '—'}
        </span>

        {/* Trailing cluster: chat id, time, chevron -- always stays together */}
        <div className="wl-trailing-group">
          {log.lChatId && (
            <span className="wl-chatid" title="WhatsApp number">
              {log.lChatId}
            </span>
          )}
          <span className="wl-time" title={formatTime(log.lCreatedAt)}>
            {formatTimeShort(log.lCreatedAt)}
          </span>
          <span className="wl-chevron">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="wl-detail">
          <div className="wl-detail-grid">
            {log.lChatId   && <DetailItem label="WhatsApp number" value={log.lChatId} mono />}
            {log.lLevel    && <DetailItem label="Level"           value={log.lLevel} />}
            {log.lTrigger  && <DetailItem label="Trigger"         value={log.lTrigger} mono />}
            {dir           && <DetailItem label="Direction"       value={dir === 'in' ? '↓ Received from user' : '↑ Sent by bot'} />}
            {log.lMessage  && <DetailItem label="Log message"     value={log.lMessage} />}
                               <DetailItem label="Timestamp"      value={formatTime(log.lCreatedAt)} />
          </div>

          {meta && Object.keys(meta).length > 0 && (
            <div className="wl-meta-block">
              <div className="wl-meta-label">Payload</div>
              <pre className="wl-pre">{JSON.stringify(meta, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value, mono }) {
  return (
    <div className="wl-detail-item">
      <span className="wl-detail-key">{label}</span>
      <span className="wl-detail-val" style={mono ? { fontFamily: 'monospace', fontSize: 12 } : {}}>
        {value}
      </span>
    </div>
  );
}

function ConfirmBar({ message, onConfirm, onCancel, loading }) {
  return (
    <div className="wl-confirm-bar">
      <span className="wl-confirm-msg">{message}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="wl-btn wl-btn--danger" onClick={onConfirm} disabled={loading}>
          {loading ? <Spinner size={13} color="#fff" /> : 'Yes, clear'}
        </button>
        <button className="wl-btn wl-btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function LogsTab({ bId }) {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [level,     setLevel]     = useState('');
  const [trigger,   setTrigger]   = useState('');
  const [search,    setSearch]    = useState('');
  const [clearing,  setClearing]  = useState(false);
  const [confirm,   setConfirm]   = useState(false);
  const [expanded,  setExpanded]  = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listLogs(bId, level, 300);
      if (res.data?.success) {
        setLogs(res.data.logs ?? []);
        setLastRefresh(new Date());
      }
    } catch {
      if (!silent) toast.error('Failed to load logs.');
    }
    if (!silent) setLoading(false);
  }, [bId, level]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      pollRef.current = setInterval(() => load(true), POLL_MS);
    }
    return () => clearInterval(pollRef.current);
  }, [autoRefresh, load]);

  async function handleClear() {
    setClearing(true);
    try {
      await clearLogs(bId);
      setLogs([]);
      setConfirm(false);
      toast.success('All logs cleared.');
    } catch {
      toast.error('Failed to clear logs.');
    }
    setClearing(false);
  }

  // Filtering
  const filtered = logs.filter(log => {
    if (trigger) {
      const meta = parseMeta(log.lMeta);
      if (trigger === 'incoming' && meta?.direction !== 'in') return false;
      if (trigger !== 'incoming' && log.lTrigger !== trigger) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const meta = parseMeta(log.lMeta);
      return (
        log.lMessage?.toLowerCase().includes(q) ||
        log.lChatId?.toLowerCase().includes(q) ||
        log.lTrigger?.toLowerCase().includes(q) ||
        meta?.message?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total:   logs.length,
    errors:  logs.filter(l => l.lLevel === 'error').length,
    in:      logs.filter(l => { const m = parseMeta(l.lMeta); return m?.direction === 'in'; }).length,
    out:     logs.filter(l => { const m = parseMeta(l.lMeta); return m?.direction === 'out'; }).length,
  };

  return (
    <div className="wl-root">
      <style>{`
        @keyframes logspin { to { transform: rotate(360deg); } }
        @keyframes wl-fade-in { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }
      `}</style>

      {/* ── Header ── */}
      <div className="wl-header">
        <div className="wl-header-left">
          <div className="wl-title">Activity Log</div>
          <div className="wl-subtitle">
            Live webhook events — messages received and responses sent by your bot
          </div>
        </div>
        <div className="wl-header-right">
          {lastRefresh && (
            <span className="wl-last-refresh">
              Updated {formatTimeShort(lastRefresh.toISOString().replace('T', ' ').slice(0, 19))}
            </span>
          )}
          <button
            className={`wl-btn ${autoRefresh ? 'wl-btn--primary' : 'wl-btn--ghost'}`}
            onClick={() => setAutoRefresh(r => !r)}
            title={autoRefresh ? 'Auto-refresh on — click to pause' : 'Auto-refresh off — click to enable'}
          >
            {autoRefresh ? '⏸ Live' : '▶ Paused'}
          </button>
          <button className="wl-btn wl-btn--ghost" onClick={() => load()} disabled={loading}>
            {loading ? <Spinner size={13} /> : '↺ Refresh'}
          </button>
          {logs.length > 0 && !confirm && (
            <button className="wl-btn wl-btn--danger-ghost" onClick={() => setConfirm(true)}>
              🗑 Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Stats row ── */}
      {!loading && logs.length > 0 && (
        <div className="wl-stats">
          <StatPill label="Total"  value={stats.total}  />
          <StatPill label="In"     value={stats.in}     color="var(--primary)" />
          <StatPill label="Out"    value={stats.out}    color="var(--info)" />
          <StatPill label="Errors" value={stats.errors} color={stats.errors > 0 ? 'var(--danger)' : 'var(--text-muted)'} />
        </div>
      )}

      {/* ── Confirm clear ── */}
      {confirm && (
        <ConfirmBar
          message="This will permanently delete all logs for this bot."
          onConfirm={handleClear}
          onCancel={() => setConfirm(false)}
          loading={clearing}
        />
      )}

      {/* ── Filters ── */}
      <div className="wl-filters">
        <input
          className="wl-search"
          type="text"
          placeholder="Search messages, numbers, triggers…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="wl-select" value={level} onChange={e => { setLevel(e.target.value); setExpanded(null); }}>
          {LEVELS.map(l => <option key={l} value={l}>{l ? l.charAt(0).toUpperCase() + l.slice(1) : 'All levels'}</option>)}
        </select>
        <select className="wl-select" value={trigger} onChange={e => { setTrigger(e.target.value); setExpanded(null); }}>
          {TRIGGERS.map(t => <option key={t} value={t}>{t || 'All types'}</option>)}
        </select>
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className="wl-empty">
          <Spinner size={24} />
          <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading logs…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="wl-empty">
          <span className="wl-empty-icon">{logs.length === 0 ? '📭' : '🔍'}</span>
          <span className="wl-empty-title">
            {logs.length === 0 ? 'No activity yet' : 'No logs match your filters'}
          </span>
          <span className="wl-empty-sub">
            {logs.length === 0
              ? 'Send a message to your WhatsApp bot and events will appear here in real time.'
              : 'Try clearing the search or changing your filters.'}
          </span>
          {search || level || trigger ? (
            <button className="wl-btn wl-btn--ghost" onClick={() => { setSearch(''); setLevel(''); setTrigger(''); }}>
              Clear filters
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="wl-count">
            Showing {filtered.length} of {logs.length} events
            {(search || level || trigger) && ' · filtered'}
          </div>
          <div className="wl-list">
            {filtered.map(log => (
              <LogRow
                key={log.lId}
                log={log}
                expanded={expanded === log.lId}
                onToggle={() => setExpanded(expanded === log.lId ? null : log.lId)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}